import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type { State } from './state'

type DownloadEntry = NonNullable<
  State['downloads'][ents.core.agent.KnowledgeBase.Management.Key]
>

/** Defines document operations scoped to one knowledge-base collection. */
export function getActionsConfig() {
  return {
    cancelDeletion: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }): State =>
        state.deletion?.entry?.loading
          ? state
          : { ...state, deletion: undefined },
    }),
    confirmDeletion: store.actions.standalone({
      data: store.actions.Data.NONE,
    }),
    delete: store.actions.request({
      input:
        store.actions.Data.asIs<ents.core.agent.KnowledgeBase.Management.File>(),
      output: store.actions.Data.asIs<void>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State => ({
          ...state,
          deletion: { fileKey: request.key },
        }),
        error: ({ state, data: { error, request } }): State => ({
          ...state,
          deletion: { entry: { error }, fileKey: request.key },
        }),
        request: ({ state, data: file }): State => ({
          ...state,
          deletion: { entry: { loading: true }, fileKey: file.key },
        }),
        success: ({ state }): State => ({ ...state, deletion: undefined }),
      },
    }),
    download: store.actions.request({
      input:
        store.actions.Data.asIs<ents.core.agent.KnowledgeBase.Management.File>(),
      output: store.actions.Data.asIs<string>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State =>
          _removeDownloadEntry(state, request.key),
        error: ({ state, data: { error, request } }): State =>
          _setDownloadEntry(state, request.key, { error }),
        request: ({ state, data: file }): State =>
          _setDownloadEntry(state, file.key, { loading: true }),
        success: ({ state, data: { request, response } }): State =>
          _setDownloadEntry(state, request.key, { data: response }),
      },
    }),
    selectForDeletion: store.actions.standalone({
      data: store.actions.Data.asIs<{
        fileKey: ents.core.agent.KnowledgeBase.Management.Key | undefined
      }>(),
      reducer: ({ state, data: { fileKey } }): State => ({
        ...state,
        deletion: fileKey ? { fileKey } : undefined,
      }),
    }),
    upload: store.actions.request({
      input: store.actions.Data.asIs<{
        file: File
        input: ents.core.agent.KnowledgeBase.Management.UploadInput
      }>(),
      output: store.actions.Data.asIs<void>(),
      reducers: {
        cancellation: ({ state }): State => ({
          ...state,
          upload: undefined,
        }),
        error: ({ state, data: { error } }): State => ({
          ...state,
          upload: { error },
        }),
        request: ({ state }): State => ({
          ...state,
          upload: { loading: true },
        }),
        success: ({ state }): State => ({
          ...state,
          upload: { data: undefined },
        }),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}

/** Sets the current operation entry for one document download. */
function _setDownloadEntry(
  state: State,
  key: ents.core.agent.KnowledgeBase.Management.Key,
  entry: DownloadEntry,
): State {
  return {
    ...state,
    downloads: { ...state.downloads, [key]: entry },
  }
}

/** Removes operation state for a cancelled document download. */
function _removeDownloadEntry(
  state: State,
  key: ents.core.agent.KnowledgeBase.Management.Key,
): State {
  const downloads = { ...state.downloads }
  delete downloads[key]
  return { ...state, downloads }
}
